import type { KeyboardEvent } from "react";
import "./AreaTabs.css";

export interface AreaTab {
  /** The URL segment under the area, e.g. `"time"` for `/payroll/time`. */
  slug: string;
  /** The tab's accessible name — exactly what ADR 0009 §1/§2 names it. */
  label: string;
}

interface AreaTabsProps {
  /** The area's own base path, e.g. `"/payroll"` — every tab lives at `${area}/${slug}`. */
  area: string;
  tabs: AreaTab[];
  /** Accessible name for the tablist itself (the area's own name). */
  label: string;
}

/**
 * The area tab strip every screen but Home carries (ADR 0009 §2) — URL-addressable,
 * keyboard-reachable (`role="tablist"`), and rendered identically on laptop and phone.
 *
 * Deliberately self-contained: it drives the browser's own History API directly instead
 * of threading a `navigate` callback through every page this job touches (most of which
 * lost their only reason to hold one when their hub/back links were retired — ADR 0009
 * §2 "no back buttons, no hub"). `pushState` doesn't fire `popstate` on its own, so a tab
 * click dispatches one manually; App.tsx's existing `popstate` listener — already there
 * for the browser's own back/forward buttons — is the one place a route change is
 * observed, so no new plumbing is needed there either.
 *
 * ACTIVATION IS AUTOMATIC (moving focus selects the tab), one of the two models the
 * WAI-ARIA Tabs pattern allows — `navigation.spec.ts`'s own arrow-key test is written to
 * accept either.
 */
export function AreaTabs({ area, tabs, label }: AreaTabsProps) {
  const trimmed = window.location.pathname.length > 1
    ? window.location.pathname.replace(/\/+$/, "")
    : window.location.pathname;
  const prefix = `${area}/`;
  const suffix = trimmed.startsWith(prefix) ? trimmed.slice(prefix.length) : "";
  // Every caller passes a non-empty, statically-defined tab list (see the exported
  // constants below) — the assertion is safe, not a runtime guess.
  const active = tabs.some((t) => t.slug === suffix) ? suffix : tabs[0]!.slug;

  function idFor(slug: string): string {
    return `area-tab-${area.replace(/^\//, "").replace(/\//g, "-")}-${slug}`;
  }

  function go(slug: string): void {
    const path = `${area}/${slug}`;
    if (trimmed === path) return;
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (index + direction + tabs.length) % tabs.length;
    const nextSlug = tabs[nextIndex]!.slug;
    go(nextSlug);
    // Focus follows the new tab once it exists — the current tab's own button may
    // belong to a component that is about to unmount (each payroll tab, for example,
    // is a whole different page component), so the target is found by id after the
    // re-render rather than held as a ref.
    requestAnimationFrame(() => {
      document.getElementById(idFor(nextSlug))?.focus();
    });
  }

  return (
    <div className="area-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => {
        const selected = tab.slug === active;
        return (
          <button
            key={tab.slug}
            id={idFor(tab.slug)}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`area-tabs__tab${selected ? " area-tabs__tab--active" : ""}`}
            onClick={() => go(tab.slug)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/** ADR 0009 §2's own spelled payroll tab URLs. */
export const PAYROLL_TABS: AreaTab[] = [
  { slug: "time", label: "Time" },
  { slug: "runs", label: "Pay runs" },
  { slug: "people", label: "People" },
  { slug: "returns", label: "Returns" },
  { slug: "leave", label: "Leave" },
];

/**
 * Setup's five tabs — the ADR 0009 tab ceiling. Access.tsx (Logins), Places.tsx,
 * JobCodes.tsx, then Schedule.tsx and TimeOff.tsx (ef-040-3), appended rather than
 * interleaved so no pre-existing spelled URL or index assumption elsewhere shifts.
 */
export const SETUP_TABS: AreaTab[] = [
  { slug: "logins", label: "Logins" },
  { slug: "places", label: "Places" },
  { slug: "job-codes", label: "Job codes" },
  { slug: "schedule", label: "Schedule" },
  { slug: "time-off", label: "Time off" },
];

/** Books' four tabs — all the Builder's own slug choice (ADR 0009 leaves this area's
 *  content out of ef-037a's stated scope). "Vendors" and "Recurring" (ef-042-6) are
 *  APPENDED after the two existing tabs rather than interleaved — same convention
 *  `SETUP_TABS` above documents for its own two additions, so no pre-existing spelled
 *  URL or index assumption elsewhere shifts. Each is its own top-level page
 *  (`BooksVendors.tsx`/`BooksRecurring.tsx`, routed at `/books/vendors`/`/books/recurring`
 *  in `App.tsx`) rather than a third/fourth branch inside `Books.tsx`'s own tab switch —
 *  this job's own file scope never lists `Books.tsx`, same pattern `Places.tsx`/
 *  `JobCodes.tsx` already use for their own Setup tabs. Four tabs, under the ADR's
 *  five-tab ceiling. */
export const BOOKS_TABS: AreaTab[] = [
  { slug: "entry", label: "Entry" },
  { slug: "activity", label: "Activity" },
  { slug: "vendors", label: "Vendors" },
  { slug: "recurring", label: "Recurring" },
  // ef-043-3: Books reaches the ADR 0009 five-tab ceiling. Appended LAST, same
  // "no pre-existing spelled URL or index assumption elsewhere shifts" discipline the
  // four entries above already follow — `shell.spec.ts`'s own `AREAS` fixture still pins
  // Books at exactly FOUR tabs (predates this job, out of this job's file scope) and
  // will need updating by its own author; flagged in this job's own return contract
  // rather than edited here (rules/code-quality.md §7).
  { slug: "reconcile", label: "Reconcile" },
];

/** Farm's four tabs — "Overview" (ADR 0009 §1's own honest empty state, unchanged),
 *  "Livestock" (ef-041-5, herd/group/animal browsing), "Fields" (ef-045-3, field +
 *  crop-year setup), then "Grain" (ef-046-4, bins/contracts/deliveries/settlement) —
 *  APPENDED last, same "append, don't insert" convention `SETUP_TABS`'s own Schedule/Time
 *  off addition already set, so Overview/Livestock/Fields' own URLs and index assumptions
 *  never shift. `shell.spec.ts`'s own `AREAS` fixture pins exactly these four, in this
 *  order. */
export const FARM_TABS: AreaTab[] = [
  { slug: "overview", label: "Overview" },
  { slug: "livestock", label: "Livestock" },
  { slug: "fields", label: "Fields" },
  { slug: "grain", label: "Grain" },
  // ef-049-2 — Farm's fifth tab, APPENDED last, same convention this constant's own
  // header documents for every prior addition.
  { slug: "milk", label: "Milk" },
];
